import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  CommunitiesListParams,
  CommunitiesListResponse,
  CommunityAction,
  CommunityDetailResponse,
  CommunityDto,
  CommunityOverviewResponse,
  CommunitySummariesResponse,
  CommunityFeatureAction,
  CommunityMembershipAction,
  CommunityOwnershipAction,
  FeaturesListResponse,
  FilePurposeType,
  HealthSnapshotsListResponse,
  MembershipsListResponse,
  NestedListParams,
  OwnershipsListResponse,
  PostAction,
  PostsListResponse,
  RequestMeta,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/communities`;

function toQuery(
  params: CommunitiesListParams | NestedListParams,
): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.sort) search.set("sort", params.sort);
  if ("q" in params && params.q) search.set("q", params.q);
  if ("status" in params && params.status) search.set("status", params.status);
  if ("market_id" in params && params.market_id) {
    search.set("market_id", params.market_id);
  }
  if ("visibility_status" in params && params.visibility_status) {
    search.set("visibility_status", params.visibility_status);
  }
  if ("health_status" in params && params.health_status) {
    search.set("health_status", params.health_status);
  }
  if ("user_id" in params && params.user_id) search.set("user_id", params.user_id);
  if ("ownership_type" in params && params.ownership_type) {
    search.set("ownership_type", params.ownership_type);
  }
  if ("author_user_id" in params && params.author_user_id) {
    search.set("author_user_id", params.author_user_id);
  }
  if ("content_type" in params && params.content_type) {
    search.set("content_type", params.content_type);
  }
  if ("creator_campaign_id" in params && params.creator_campaign_id) {
    search.set("creator_campaign_id", params.creator_campaign_id);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createCommunitiesApi(client: ApiClient) {
  return {
    list(params: CommunitiesListParams = {}) {
      return client.get<CommunitiesListResponse>(`${BASE}${toQuery(params)}`);
    },

    getDetail(communityId: string) {
      return client.get<CommunityDetailResponse>(`${BASE}/${communityId}`);
    },

    /** What Play stores about a page of communities: members, owner, joining, reports. */
    summaries(ids: string[]) {
      return client.post<CommunitySummariesResponse>(`${BASE}/summaries`, { ids });
    },

    /** The full read model for one community. */
    overview(communityId: string) {
      return client.get<CommunityOverviewResponse>(`${BASE}/${communityId}/overview`);
    },

    create(body: {
      name: string;
      market_id?: string | null;
      description?: string | null;
      category?: string | null;
      visibility_status?: string;
      status?: "draft" | "active";
    }) {
      return client.post<{ community: CommunityDto; meta: RequestMeta }>(
        BASE,
        body,
        newIdempotencyKey(),
      );
    },

    updateProfile(
      communityId: string,
      body: {
        name?: string;
        market_id?: string | null;
        description?: string | null;
        category?: string | null;
        visibility_status?: string;
      },
    ) {
      return client.patch<{ community: CommunityDto; meta: RequestMeta }>(
        `${BASE}/${communityId}`,
        body,
        newIdempotencyKey(),
      );
    },

    applyAction(
      communityId: string,
      action: CommunityAction,
      body: { reason?: string; approval_id?: string; high_impact?: boolean },
    ) {
      return client.post<{ community: CommunityDto; meta: RequestMeta }>(
        `${BASE}/${communityId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    attachFile(
      communityId: string,
      body: { file_asset_id: string; purpose_type: FilePurposeType },
    ) {
      return client.post(
        `${BASE}/${communityId}/files/attach`,
        body,
        newIdempotencyKey(),
      );
    },

    listMemberships(communityId: string, params: NestedListParams = {}) {
      return client.get<MembershipsListResponse>(
        `${BASE}/${communityId}/memberships${toQuery(params)}`,
      );
    },

    createMembership(
      communityId: string,
      body: {
        user_id: string;
        mode?: "invite" | "request";
        membership_type?: string;
      },
    ) {
      return client.post(
        `${BASE}/${communityId}/memberships`,
        body,
        newIdempotencyKey(),
      );
    },

    applyMembershipAction(
      membershipId: string,
      action: CommunityMembershipAction,
      body: { reason?: string },
    ) {
      return client.post(
        `${BASE}/memberships/${membershipId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    /** Takes a person out of the community in the app; they are told why (i139). */
    removeMember(communityId: string, userId: string, reason: string) {
      return client.post(
        `${BASE}/${communityId}/members/${userId}/remove`,
        { reason },
        newIdempotencyKey(),
      );
    },

    listOwnerships(communityId: string, params: NestedListParams = {}) {
      return client.get<OwnershipsListResponse>(
        `${BASE}/${communityId}/ownerships${toQuery(params)}`,
      );
    },

    createOwnership(
      communityId: string,
      body: {
        user_id: string;
        ownership_type: string;
        status?: "proposed" | "active";
      },
    ) {
      return client.post(
        `${BASE}/${communityId}/ownerships`,
        body,
        newIdempotencyKey(),
      );
    },

    applyOwnershipAction(
      ownershipId: string,
      action: CommunityOwnershipAction,
      body: { reason?: string; approval_id?: string; new_user_id?: string },
    ) {
      return client.post(
        `${BASE}/ownerships/${ownershipId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listPosts(communityId: string, params: NestedListParams = {}) {
      return client.get<PostsListResponse>(
        `${BASE}/${communityId}/posts${toQuery(params)}`,
      );
    },

    createPost(
      communityId: string,
      body: {
        content_type: string;
        content_reference: string;
        author_user_id?: string;
      },
    ) {
      return client.post(
        `${BASE}/${communityId}/posts`,
        body,
        newIdempotencyKey(),
      );
    },

    applyPostAction(
      postId: string,
      action: PostAction,
      body: {
        reason?: string;
        content_reference?: string;
        content_type?: string;
      },
    ) {
      return client.post(
        `${BASE}/posts/${postId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listHealthSnapshots(communityId: string, params: NestedListParams = {}) {
      return client.get<HealthSnapshotsListResponse>(
        `${BASE}/${communityId}/health-snapshots${toQuery(params)}`,
      );
    },

    createHealthSnapshot(
      communityId: string,
      body: {
        status: string;
        measured_at?: string;
        metrics_snapshot?: Record<string, unknown>;
        risk_summary?: string | null;
        reviewed_by_user_id?: string | null;
      },
    ) {
      return client.post(
        `${BASE}/${communityId}/health-snapshots`,
        body,
        newIdempotencyKey(),
      );
    },

    listFeatures(communityId: string, params: NestedListParams = {}) {
      return client.get<FeaturesListResponse>(
        `${BASE}/${communityId}/features${toQuery(params)}`,
      );
    },

    createFeature(
      communityId: string,
      body: {
        creator_campaign_id?: string | null;
        starts_at?: string | null;
        ends_at?: string | null;
        performance_snapshot?: Record<string, unknown>;
      },
    ) {
      return client.post(
        `${BASE}/${communityId}/features`,
        body,
        newIdempotencyKey(),
      );
    },

    applyFeatureAction(
      featureId: string,
      action: CommunityFeatureAction,
      body: {
        reason?: string;
        approval_id?: string;
        starts_at?: string | null;
        ends_at?: string | null;
      },
    ) {
      return client.post(
        `${BASE}/features/${featureId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type CommunitiesApi = ReturnType<typeof createCommunitiesApi>;
